import apiClient from './client';
import type { ApiResponse } from './types';

export interface AIQuery {
  id: string;
  organization_id: string;
  user_id: string;
  query_text: string;
  query_type: string;
  context_data?: string;
  response_text: string;
  response_data?: string;
  confidence_score: number;
  model_used: string;
  tokens_used: number;
  processing_time_ms: number;
  feedback_rating?: number;
  feedback_comment?: string;
  is_helpful?: boolean;
  created_at: number;
  updated_at: number;
}

export interface AIInsight {
  id: string;
  organization_id: string;
  insight_type: string;
  insight_category: string;
  title: string;
  description: string;
  data_source: string;
  data_period_start: number;
  data_period_end: number;
  severity: string;
  priority: number;
  impact_score: number;
  confidence_score: number;
  supporting_data?: string;
  recommended_actions?: string;
  related_entities?: string;
  is_dismissed: boolean;
  dismissed_by?: string;
  dismissed_at?: number;
  dismissed_reason?: string;
  is_actioned: boolean;
  actioned_at?: number;
  action_taken?: string;
  expires_at?: number;
  generated_by: string;
  model_used: string;
  created_at: number;
  updated_at: number;
}

export interface AIAnomaly {
  id: string;
  organization_id: string;
  anomaly_type: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  detected_value: number;
  expected_value: number;
  variance_percentage: number;
  standard_deviations: number;
  severity: string;
  description: string;
  detection_method: string;
  baseline_data?: string;
  is_false_positive: boolean;
  marked_false_positive_by?: string;
  marked_false_positive_at?: number;
  is_investigated: boolean;
  investigated_by?: string;
  investigated_at?: number;
  investigation_notes?: string;
  resolution_status: string;
  resolved_at?: number;
  model_used: string;
  confidence_score: number;
  created_at: number;
  updated_at: number;
}

export interface AIAction {
  id: string;
  organization_id: string;
  insight_id?: string;
  anomaly_id?: string;
  action_type: string;
  action_category: string;
  title: string;
  description: string;
  priority: number;
  estimated_impact?: string;
  estimated_value?: number;
  entity_type?: string;
  entity_id?: string;
  assigned_to?: string;
  assigned_at?: number;
  due_date?: number;
  status: string;
  completed_by?: string;
  completed_at?: number;
  completion_notes?: string;
  dismissed_by?: string;
  dismissed_at?: number;
  dismissal_reason?: string;
  action_data?: string;
  created_at: number;
  updated_at: number;
}

export interface AIConfiguration {
  id: string;
  organization_id: string;
  is_enabled: boolean;
  provider: string;
  model_name: string;
  api_endpoint?: string;
  max_tokens: number;
  temperature: number;
  enable_insights: boolean;
  insights_frequency: string;
  enable_anomaly_detection: boolean;
  anomaly_sensitivity: string;
  enable_action_suggestions: boolean;
  auto_assign_actions: boolean;
  daily_query_limit: number;
  monthly_budget_usd?: number;
  current_month_spend: number;
  metadata?: string;
  created_at: number;
  updated_at: number;
}

// Query Operations
export const processQuery = async (queryText: string, queryType: string, contextData?: Record<string, any>): Promise<AIQuery> => {
  const response = await apiClient.post<ApiResponse<AIQuery>>('/ai/query', {
    query_text: queryText,
    query_type: queryType,
    context_data: contextData,
  });
  return response.data.data;
};

export const listQueries = async (params?: {
  user_id?: string;
  query_type?: string;
  limit?: number;
  offset?: number;
}): Promise<AIQuery[]> => {
  const response = await apiClient.get<ApiResponse<AIQuery[]>>('/ai/queries', { params });
  return response.data.data;
};

export const updateQueryFeedback = async (
  queryId: string,
  rating: number,
  comment: string,
  isHelpful: boolean
): Promise<void> => {
  await apiClient.put(`/ai/queries/${queryId}/feedback`, {
    rating,
    comment,
    is_helpful: isHelpful,
  });
};

// Insight Operations
export const generateInsights = async (dataSource: string, startDate: number, endDate: number): Promise<AIInsight[]> => {
  const response = await apiClient.post<ApiResponse<AIInsight[]>>('/ai/insights/generate', {
    data_source: dataSource,
    start_date: startDate,
    end_date: endDate,
  });
  return response.data.data;
};

export const listInsights = async (params?: {
  insight_type?: string;
  category?: string;
  severity?: string;
  is_dismissed?: boolean;
  limit?: number;
  offset?: number;
}): Promise<AIInsight[]> => {
  const response = await apiClient.get<ApiResponse<AIInsight[]>>('/ai/insights', { params });
  return response.data.data;
};

export const getInsight = async (insightId: string): Promise<AIInsight> => {
  const response = await apiClient.get<ApiResponse<AIInsight>>(`/ai/insights/${insightId}`);
  return response.data.data;
};

export const dismissInsight = async (insightId: string, reason: string): Promise<void> => {
  await apiClient.post(`/ai/insights/${insightId}/dismiss`, { reason });
};

// Anomaly Operations
export const listAnomalies = async (params?: {
  anomaly_type?: string;
  severity?: string;
  resolution_status?: string;
  is_false_positive?: boolean;
  limit?: number;
  offset?: number;
}): Promise<AIAnomaly[]> => {
  const response = await apiClient.get<ApiResponse<AIAnomaly[]>>('/ai/anomalies', { params });
  return response.data.data;
};

export const updateAnomalyInvestigation = async (anomalyId: string, notes: string): Promise<void> => {
  await apiClient.post(`/ai/anomalies/${anomalyId}/investigate`, { notes });
};

export const markAnomalyFalsePositive = async (anomalyId: string): Promise<void> => {
  await apiClient.post(`/ai/anomalies/${anomalyId}/false-positive`);
};

// Action Operations
export const listActions = async (params?: {
  status?: string;
  category?: string;
  assigned_to?: string;
  limit?: number;
  offset?: number;
}): Promise<AIAction[]> => {
  const response = await apiClient.get<ApiResponse<AIAction[]>>('/ai/actions', { params });
  return response.data.data;
};

export const assignAction = async (actionId: string, userId: string, dueDate?: number): Promise<void> => {
  await apiClient.post(`/ai/actions/${actionId}/assign`, {
    user_id: userId,
    due_date: dueDate,
  });
};

export const completeAction = async (actionId: string, notes: string): Promise<void> => {
  await apiClient.post(`/ai/actions/${actionId}/complete`, { notes });
};

// Configuration Operations
export const getConfiguration = async (): Promise<AIConfiguration> => {
  const response = await apiClient.get<ApiResponse<AIConfiguration>>('/ai/configuration');
  return response.data.data;
};

export const updateConfiguration = async (config: Partial<AIConfiguration>): Promise<void> => {
  await apiClient.put('/ai/configuration', config);
};
